import { useCallback, useEffect, useState } from "react";
import type { ProbeResult } from "../api";
import { useDeviceAction } from "./useDeviceAction";
import type { SectionHandle } from "./useSection";

// Schreiben auf Probe mit Auto-Rollback (docs/wire-protocol-v1.md): `<x>_set`
// wendet testweise an, ohne `<x>_confirm` stellt der Router selbst den alten
// Stand wieder her. Firewall und WireGuard-Uplink teilen sich diesen
// Zwei-Schritt — deshalb ein Hook statt zweier Kopien. Der Countdown hier ist
// reine Anzeige: die Frist läuft auf dem Router, nicht im Browser.

export type ProbePhase = "idle" | "probe" | "kept" | "reverted";

export interface ProbeConfig {
  /** Action, die auf Probe schreibt (z.B. `firewall_set`). */
  setAction: string;
  /** Action, die die Probe dauerhaft übernimmt (z.B. `firewall_confirm`). */
  confirmAction: string;
  /** Frist in Sekunden, bis der Router von selbst zurückrollt. */
  revertIn: number;
}

export function useConfirmProbe<T>(
  deviceId: string,
  section: SectionHandle<T>,
  { setAction, confirmAction, revertIn }: ProbeConfig,
) {
  const { busy, note, setNote, run } = useDeviceAction(deviceId);
  const [phase, setPhase] = useState<ProbePhase>("idle");
  const [token, setToken] = useState<string | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(revertIn);

  // Läuft die Frist ab, hat der Router von selbst zurückgerollt → Sektion neu
  // laden, damit die Anzeige nicht den verworfenen Stand zeigt.
  useEffect(() => {
    if (deadline === null) return;
    const tick = () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left > 0) return;
      setDeadline(null);
      setToken(null);
      setPhase("reverted");
      void section.refresh();
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [deadline, section]);

  /**
   * Auf Probe anwenden. `params` sind die flachen Wire-Params der Action,
   * `optimistic` der Sektionsstand, der während der Probe gilt.
   */
  const apply = useCallback(
    async (params: Record<string, unknown>, optimistic?: T): Promise<ProbeResult | null> => {
      const data = await run<ProbeResult>(setAction, { ...params, revert_in: revertIn });
      if (!data) return null;
      if (optimistic) section.patch(optimistic);
      // Ohne Token hat der Server direkt geschrieben statt auf Probe. Das darf
      // er entscheiden: nur er weiß, ob die Änderung etwas anfasst, das die Box
      // online hält (`wifi_set` probt für WLAN-Uplinks, für einen AP nicht).
      if (!data.confirmToken) {
        setPhase("idle");
        return data;
      }
      setToken(data.confirmToken);
      setDeadline(Date.now() + (data.revertIn || revertIn) * 1000);
      setPhase("probe");
      return data;
    },
    [run, section, setAction, revertIn],
  );

  const keep = useCallback(async () => {
    if (!token) return;
    const ok = await run<{ committed: boolean }>(confirmAction, { confirm_token: token });
    if (!ok) return;
    setDeadline(null);
    setToken(null);
    setPhase("kept");
  }, [run, confirmAction, token]);

  const dismiss = useCallback(() => setPhase("idle"), []);

  return {
    phase,
    secondsLeft,
    note,
    setNote,
    /** Die Probe wird gerade geschrieben. */
    applying: busy === setAction,
    /** „Behalten“ läuft gerade. */
    keeping: busy === confirmAction,
    apply,
    keep,
    dismiss,
  };
}
